<template>
    <div class="modal">
        <span class="close" @click="$emit('exitModal')">&times;</span>
        <h2>Save Changes</h2>

        <div class="changes-section" v-if="hasDifferences">
            <div class="diff-container">
                <div v-if="addedBenefits.length > 0" class="added-benefits">
                    <h4>Benefits Added:</h4>
                    <div v-for="benefit in addedBenefits" :key="benefit.id" class="benefit-change added">
                        <span class="benefit-name">{{ benefit.name }}</span>
                        <span class="benefit-code" v-if="benefit.code">({{ benefit.code }})</span>
                    </div>
                </div>
                <div v-if="removedBenefits.length > 0" class="removed-benefits">
                    <h4>Benefits Removed:</h4>
                    <div v-for="benefit in removedBenefits" :key="benefit.id" class="benefit-change removed">
                        <span class="benefit-name">{{ benefit.name }}</span>
                        <span class="benefit-code" v-if="benefit.code">({{ benefit.code }})</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="no-changes">
            No changes have been made to the plan's benefits.
        </div>

        <div class="checkbox-container input-class no-hover-border" v-if="addedBenefits.length > 0 && integrationsEnabled">
            <label class="checkbox-label">Log added benefits on plan patients?</label>
            <div class="checkbox grow-medium" @click="toggleLogBenefits">
                <div class="checkbox-fill" v-if="logBenefits">
                    <p class="checkbox-text no-select">✓</p>
                </div>
            </div>
        </div>

        <button 
            v-if="addedBenefits.length > 0 || removedBenefits.length > 0" 
            class="save-btn hover-grow" 
            @click="checkAndSave"
            :disabled="isSaving"
        >
            <div v-if="isSaving" class="button-content">
                <div class="spinner"></div>
                <span>Saving...</span>
            </div>
            <span v-else>Save Changes</span>
        </button>
        <!-- Warning Modal -->
        <div class="modal-overlay" v-if="showDuplicateWarning" @click.self="showDuplicateWarning = false">
            <DuplicateCodeWarningModal 
                @exitModal="showDuplicateWarning = false"
                @continue="handleSave"
            />
        </div>
    </div>

    
</template>

<script>
import DuplicateCodeWarningModal from './DuplicateCodeWarningModal.vue';

export default {
    components: {
        DuplicateCodeWarningModal
    },
    props: {
        originalPlan: {
            type: Object,
            required: true
        },
        workingPlan: {
            type: Object,
            required: true
        },
        integrationsEnabled: {
            type: Boolean,
            required: true
        },
        allBenefits: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            logBenefits: false,
            showDuplicateWarning: false,
            isSaving: false
        }
    },
    computed: {
        addedBenefits() {
            if (!this.workingPlan?.benefits || !this.originalPlan?.benefits) return [];
            return this.workingPlan.benefits.filter(workingBenefit => 
                !this.originalPlan.benefits.some(origBenefit => 
                    origBenefit.id === workingBenefit.id
                )
            ).map(benefit => ({
                id: benefit.id,
                name: benefit.name,
                code: benefit.code,
                payout: benefit.payout || benefit.price || 0
            }));
        },
        removedBenefits() {
            if (!this.workingPlan?.benefits || !this.originalPlan?.benefits) return [];
            return this.originalPlan.benefits.filter(origBenefit => 
                !this.workingPlan.benefits.some(workingBenefit => 
                    workingBenefit.id === origBenefit.id
                )
            );
        },
        hasDifferences() {
            return this.addedBenefits.length > 0 || this.removedBenefits.length > 0;
        }
    },
    methods: {
        toggleLogBenefits() {
            this.logBenefits = !this.logBenefits;
        },
        checkAndSave() {
            if (this.hasDuplicateCodes()) {
                this.showDuplicateWarning = true;
            } else {
                this.handleSave();
            }
        },
        hasDuplicateCodes() {
            // Get all existing benefit codes (split by comma and trimmed)
            const existingCodes = this.allBenefits
                .filter(b => !this.removedBenefits.some(rb => rb.id === b.id) && 
                           !this.addedBenefits.some(ab => ab.id === b.id))
                .flatMap(b => (b.code || '').split(',').map(c => c.trim()))
                .filter(code => code !== '');

            console.log('Existing codes:', existingCodes);

            // Check each added benefit's codes against existing codes
            const duplicates = this.addedBenefits.reduce((acc, newBenefit) => {
                const newCodes = (newBenefit.code || '').split(',').map(c => c.trim()).filter(c => c !== '');
                console.log(`Checking codes for ${newBenefit.name}:`, newCodes);
                
                const duplicateCodes = newCodes.filter(code => {
                    const isDuplicate = existingCodes.includes(code);
                    if (isDuplicate) {
                        console.log(`Found duplicate code ${code} for ${newBenefit.name}`);
                    }
                    return isDuplicate;
                });

                if (duplicateCodes.length > 0) {
                    acc.push({
                        benefitName: newBenefit.name,
                        codes: duplicateCodes
                    });
                }
                return acc;
            }, []);

            if (duplicates.length > 0) {
                console.log('Duplicate codes found:');
                duplicates.forEach(dup => {
                    console.log(`${dup.benefitName}: ${dup.codes.join(', ')}`);
                });
                return true;
            }

            console.log('No duplicate codes found');
            return false;
        },
        async handleSave() {
            this.isSaving = true;
            const changes = {
                planID: this.workingPlan.id,
                benefitsToAdd: this.addedBenefits.map(b => ({
                    id: b.id,
                    payout: b.payout
                })),
                benefitsToRemove: this.removedBenefits.map(b => b.id),
                syncBenefits: this.logBenefits
            };
            this.$emit('saveChanges', changes);
        }
    }
}
</script>

<style scoped>
.modal {
    position: relative;
    width: 30%;
    background-color: white;
    padding: 2em;
    border-radius: 20px;
    border: 3px solid var(--color-dark-blue);
    outline: 2px solid white;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.close {
    position: absolute;
    right: 25px;
    top: 5px;
    color: var(--color-medium-red);
    font-size: 2.5em;
    cursor: pointer;
    font-family: sans-serif;
}

h2 {
    color: var(--color-dark-blue);
    margin-bottom: 1.5em;
    font-size: 2em;
}

h3 {
    color: var(--color-dark-blue);
    margin-bottom: 1em;
    font-size: 1.2em;
}

h4 {
    color: var(--color-dark-blue);
    margin-bottom: 0.25em;
    margin-top: 0.25em;
    font-size: 1.1em;
}

.changes-section {
    width: 100%;
    margin-bottom: 2em;
}

.diff-container {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.benefit-change {
    padding: 0.5em 1em;
    border-radius: 8px;
    margin: 0.5em 0;
}

.added {
    background-color: #e6ffe6;
    border: 1px solid #90EE90;
}

.removed {
    background-color: #ffe6e6;
    border: 1px solid #FFB6C6;
}

.benefit-name {
    font-weight: 600;
    margin-right: 0.5em;
}

.benefit-code {
    color: var(--color-bright-blue);
    font-family: monospace;
}

.no-changes {
    color: var(--color-medium-gray);
    margin-bottom: 2em;
    font-style: italic;
}

.checkbox-container {
    border: none;
    display: flex;
    align-items: center;
    gap: 1em;
    margin-bottom: 2em;
}

.no-hover-border:hover {
    border: none;
}

.checkbox {
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid var(--color-dark-blue);
    border-radius: 5px;
    cursor: pointer;
}

.checkbox-fill {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 23px;
    height: 23px;
    background-color: var(--color-dark-blue);
    border-radius: 3px;
    color: white;
}

.checkbox-text {
    margin: 0;
    font-size: 1.2em;
    left: 2px;
    top: -3px;
}

.checkbox-label {
    color: var(--color-dark-blue);
    font-size: 1.2em;
}

.grow-medium {
    transition: all 0.2s ease-in-out;
}

.grow-medium:hover {
    transform: scale(1.1);
}

.no-select {
    user-select: none;
}

.save-btn {
    background-color: transparent;
    border: 2px solid var(--color-light-blue);
    color: var(--color-dark-blue);
    font-weight: 900;
    padding: 0.5em 2em;
    border-radius: 25px;
    font-size: 1.2em;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    min-width: 180px;
}

.save-btn:disabled {
    border-color: var(--color-medium-light-gray);
    color: var(--color-medium-gray);
    cursor: not-allowed;
}

.save-btn:disabled:hover {
    transform: none;
    border-color: var(--color-medium-light-gray);
}

.button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
}

.spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top-color: var(--color-medium-gray);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}
</style> 